import type { OrderGateInput, OrderGates } from './types';

/**
 * What the customer is allowed to do with this product.
 *
 * There are two different orders here and they are not the same question:
 *
 *   - `canOrder` is a real, priced order. It needs stock, a price, and a
 *     complete selection.
 *   - `canPlaceholder` is an intent — placed when pricing is hidden or the
 *     product is unavailable — and needs only a complete selection.
 *
 * `hidePrice` defaults true for this org, so the intent path is the normal one
 * in production, not the exception.
 */
export const computeOrderGates = (input: OrderGateInput): OrderGates => {
  const {
    hidePrice,
    deliveryType,
    location,
    addressCount,
    addressId,
    palletId,
    selectedLoad,
    requiresPo,
    poNumber,
  } = input;

  const outOfStock = !!location?.outOfStock;
  const lowStock = !!location?.lowStock;

  // Hiding prices makes everything "not available" for ordering purposes:
  // there is no price to charge against, so every order becomes an intent.
  const isAvailable = !!location?.isAvailable && !outOfStock && !hidePrice;

  const hasAddress = addressCount > 0 && !!addressId;
  const hasFulfillmentMethod = !!selectedLoad?.orgLoadOption?.id;

  /*
   * NOTE: `Number(null)` is 0, which is finite — so a load whose price is
   * explicitly null counts as priced, while a missing price (undefined → NaN)
   * does not. Extracted verbatim; `hasPrice` in the tests pins both.
   */
  const hasPrice = Number.isFinite(Number(selectedLoad?.price));

  const hasPalletIfNeeded = deliveryType !== 'PALLET' || !!palletId;

  // A flagged sub-location needs a non-blank PO before either order can go.
  const hasPoIfRequired = !requiresPo || !!poNumber?.trim();

  return {
    outOfStock,
    lowStock,
    isAvailable,
    hasAddress,
    hasFulfillmentMethod,
    hasPrice,
    hasPoIfRequired,
    canOrder:
      isAvailable &&
      hasFulfillmentMethod &&
      hasPrice &&
      hasAddress &&
      hasPalletIfNeeded &&
      hasPoIfRequired,
    canPlaceholder: hasFulfillmentMethod && hasAddress && hasPalletIfNeeded && hasPoIfRequired,
  };
};

/**
 * The first thing stopping a submit, or null. The drawer toasts this before
 * falling back to the silent gates above.
 */
export const submitBlockReason = (input: {
  loadOptionId?: string;
  addressId: string;
  deliveryType?: string | null;
  palletId: string;
  requiresPo?: boolean;
  poNumber?: string;
}): string | null => {
  if (!input.loadOptionId) {
    return 'Select a fulfilment method.';
  }

  if (!input.addressId) {
    return 'Select a delivery address.';
  }

  if (input.deliveryType === 'PALLET' && !input.palletId) {
    return 'Select a pallet size.';
  }

  if (input.requiresPo && !input.poNumber?.trim()) {
    return 'A PO number is required for this location.';
  }

  return null;
};

/** Currency for the drawer. Whole dollars — these are load prices, not invoices. */
export const fmtMoney = (n: unknown, currency = 'USD'): string => {
  const num = Number(n);

  return isFinite(num)
    ? num.toLocaleString(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      })
    : '—';
};
